/*
  Header chat icon (snippets/sbs-header-chat.liquid).

  Shows with the other header icons. Opens the Shopify Inbox chat (<shopify-chat>, added by the Inbox app embed)
  with its public show() and close(); Shopify only switches the chat on once the page has loaded, so a tap before
  then opens it as soon as it's ready. When a reply from the team arrives while the chat isn't being looked at,
  shows a dot and a "New reply" bubble that stays until tapped (both kept across pages until the chat is opened);
  the automatic answer to a customer's first message doesn't count if it lands just after they close the chat.
  Otherwise, once per visit and only within the team's hours (Theme settings → Live chat, Melbourne time; rule in
  sbs-header-chat-logic.js, tested), nudges: the icon hops and a "Need help?" bubble points at it. A bubble only
  shows while the icon is on screen: the phone menu hides the header icons, so it hides the bubble too, and an
  unread reply's bubble comes back when the menu closes. Shopify's floating
  button is hidden by assets/sbs-header-chat.css; if the chat doesn't arrive or can't be opened from here,
  html.sbs-chat-fallback brings it back and hides this icon.
*/
const READY_CLASS = 'sbs-chat-in-header'
const FALLBACK_CLASS = 'sbs-chat-fallback'
const NUDGED_KEY = 'sbs-chat-nudged'
const UNREAD_KEY = 'sbs-chat-unread'
// Shopify counts unread replies in the tab title, "(2) Page title", and clears it once the chat is being read.
const TITLE_COUNT = /^\((\d+)\)\s/
// Shopify Inbox answers a customer's first message automatically. If that answer lands within 10 seconds of the
// first close after the message, it isn't announced. Values: 'pending' (first message sent), the window's end
// time while it's open, 'done'.
const AUTO_KEY = 'sbs-chat-autoreply'
const AUTO_WINDOW = 10000
// The reply event and the tab-title count both report the same reply; signals this close together are one reply.
const DUPLICATE_GAP = 2000
const NUDGE_DELAY = 4000
const TIP_TIME = 6000
const HOP_LANDS = 650
const TYPING_TIME = 1100
// A moment's wait before announcing a reply, in case the chat gets opened for it in the meantime.
const REPLY_DELAY = 600
const WAIT_LIMIT = 15000

let chatPromise = null
let nudgeTaken = false
let openRequested = false
let lastReplySignal = 0

// The header is a theme island (<is-land on:idle>, assets/is-land.min.js): until it hydrates around page load, this
// element is swapped for a look-alike placeholder that has no click handler. A tap on that placeholder is remembered
// here and opens the chat once the element is back and the chat is ready.
document.addEventListener('click', (event) => {
  const button = event.target.closest('.sbs-header-chat__button')
  if (button && !button.closest('sbs-header-chat')) {
    openRequested = true
    button.setAttribute('aria-busy', 'true')
  }
})

// The chat element once its script has defined it, or null if it doesn't arrive in time.
function waitForChat() {
  chatPromise ??= new Promise((resolve) => {
    let settled = false
    const done = (chat) => {
      if (settled) return
      settled = true
      observer.disconnect()
      clearTimeout(timer)
      resolve(chat)
    }
    const check = () => {
      const chat = document.querySelector('shopify-chat')
      if (chat) customElements.whenDefined('shopify-chat').then(() => done(chat))
      return Boolean(chat)
    }
    const observer = new MutationObserver(() => {
      if (check()) observer.disconnect()
    })
    const timer = setTimeout(() => done(null), WAIT_LIMIT)
    if (!check()) observer.observe(document.body, { childList: true, subtree: true })
  })
  return chatPromise
}

// Unread replies, for this visit: set when one arrives unseen, cleared when the chat is opened and looked at.
function readUnread() {
  try {
    return sessionStorage.getItem(UNREAD_KEY) === '1'
  } catch {
    return false
  }
}

function writeUnread(unread) {
  try {
    if (unread) sessionStorage.setItem(UNREAD_KEY, '1')
    else sessionStorage.removeItem(UNREAD_KEY)
  } catch {}
}

function readAuto() {
  try {
    return sessionStorage.getItem(AUTO_KEY)
  } catch {
    return 'done'
  }
}

function writeAuto(value) {
  try {
    sessionStorage.setItem(AUTO_KEY, value)
  } catch {}
}

function titleCount() {
  const match = document.title.match(TITLE_COUNT)
  return match ? Number(match[1]) : 0
}

class SbsHeaderChat extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.sbs-header-chat__button')
    this.dot = this.querySelector('.sbs-header-chat__dot')
    this.tip = this.querySelector('.sbs-header-chat__tip')
    this.observed = new WeakSet()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.button.addEventListener('click', () => this.toggleChat(), { signal })
    this.tip.addEventListener('click', () => this.toggleChat(), { signal })

    // The icon leaving the screen (the phone menu sets the header icons to display: none) takes the bubble with it;
    // coming back brings an unread reply's bubble back.
    this.iconShown = this.iconOnScreen()
    this.iconObserver = new ResizeObserver(() => {
      const shown = this.iconOnScreen()
      if (shown === this.iconShown) return
      this.iconShown = shown
      if (!shown) this.hideTip()
      else if (this.chat && !this.chat.open && readUnread()) this.announceReply()
    })
    this.iconObserver.observe(this.button)

    waitForChat().then((chat) => this.wire(chat))
  }

  disconnectedCallback() {
    this.abortController.abort()
    this.observer?.disconnect()
    this.titleObserver?.disconnect()
    this.iconObserver?.disconnect()
  }

  iconOnScreen() {
    return this.button.offsetWidth > 0
  }

  wire(chat) {
    if (!this.isConnected) return
    this.button.removeAttribute('aria-busy')
    if (!chat || typeof chat.show !== 'function' || typeof chat.close !== 'function') {
      document.documentElement.classList.add(FALLBACK_CLASS)
      return
    }
    this.chat = chat
    this.wasOpen = Boolean(chat.open)
    document.documentElement.classList.add(READY_CLASS)
    this.classList.add('is-ready')

    // Shadow DOM changes (the chat opening and closing) resync the dot and aria-expanded.
    this.observer = new MutationObserver(() => this.sync())
    // A reply: Shopify's unread count in the tab title going up, or its reply event (observeShadows).
    const title = document.querySelector('title')
    if (title) {
      let count = titleCount()
      this.titleObserver = new MutationObserver(() => {
        const next = titleCount()
        if (next > count) this.onReply()
        count = next
      })
      this.titleObserver.observe(title, { childList: true, characterData: true, subtree: true })
    }
    document.addEventListener(
      'visibilitychange',
      () => {
        this.sync()
        if (document.visibilityState === 'visible' && this.replyPending) this.announceReply()
      },
      { signal: this.abortController.signal }
    )
    this.sync()
    if (this.openWhenReady || openRequested) {
      this.openWhenReady = false
      openRequested = false
      this.toggleChat()
      return
    }
    // A reply from earlier in the visit that hasn't been read: announce it instead of the nudge.
    if (readUnread()) {
      nudgeTaken = true
      this.announceReply(1500)
      return
    }
    this.nudge()
  }

  // The customer is looking at the open chat.
  reading() {
    return Boolean(this.chat.open) && document.visibilityState === 'visible'
  }

  onReply() {
    const now = Date.now()
    if (now - lastReplySignal < DUPLICATE_GAP) return
    lastReplySignal = now

    const auto = readAuto()
    if (this.reading()) {
      // Seen in the open chat: if it was the automatic answer, there's nothing left to ignore.
      if (auto && auto !== 'done') writeAuto('done')
      return
    }
    if (auto && auto !== 'pending' && auto !== 'done') {
      writeAuto('done')
      if (now < Number(auto)) return // the automatic answer, just after the chat was closed
    }
    writeUnread(true)
    this.sync()
    this.replyPending = true
    if (document.visibilityState === 'visible') this.announceReply()
  }

  // The "New reply" bubble, unless the chat has been opened in the meantime.
  announceReply(delay = REPLY_DELAY) {
    clearTimeout(this.replyTimer)
    this.replyTimer = setTimeout(() => {
      if (!this.isConnected || this.chat.open || !readUnread()) return
      if (!this.iconOnScreen()) return // shown when the icon is back (iconObserver)
      this.replyPending = false
      this.showTip('reply')
    }, delay)
  }

  // The customer sent a message: the first one in the visit gets an automatic answer.
  onSent() {
    if (!readAuto()) writeAuto('pending')
  }

  // The first close after that message opens the 10-second window for the automatic answer.
  onClose() {
    if (readAuto() === 'pending') writeAuto(String(Date.now() + AUTO_WINDOW))
  }

  sync() {
    if (!this.chat) return
    this.observeShadows(this.chat)
    const open = Boolean(this.chat.open)
    if (this.wasOpen && !open) this.onClose()
    this.wasOpen = open
    if (this.reading()) writeUnread(false)
    if (this.chat.open) this.hideTip()
    const unread = readUnread()
    this.dot.hidden = !unread
    // The lockout page shows a label beside the icon ("Live chat"); the button's name uses the same words.
    const name = this.button.dataset.label || 'Chat with us'
    this.button.setAttribute('aria-label', unread ? `${name}, unread replies` : name)
    this.button.setAttribute('aria-expanded', String(Boolean(this.chat.open)))
  }

  // Mutation observers don't cross shadow roots, so watch each one the chat has (they can appear later).
  observeShadows(host, depth = 0) {
    const root = host.shadowRoot
    if (!root || depth > 3) return
    if (!this.observed.has(root)) {
      this.observed.add(root)
      this.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open'] })
      root.addEventListener('shopify-chat:inbox-message-received', () => this.onReply())
      root.addEventListener('shopify-chat:user-sent', () => this.onSent())
    }
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) this.observeShadows(el, depth + 1)
    })
  }

  toggleChat() {
    if (!this.chat) {
      // The chat isn't switched on yet: open it when it is.
      this.openWhenReady = true
      this.button.setAttribute('aria-busy', 'true')
      return
    }
    this.hideTip()
    if (this.chat.open) {
      this.chat.close()
    } else {
      // Desktop: the chat opens under the header (assets/sbs-header-chat.css reads --sbs-chat-top). Pages without the
      // site header (the lockout page) mark their own top bar with data-sbs-chat-anchor.
      const header = document.querySelector('.site-header, [data-sbs-chat-anchor]')
      const bottom = header ? header.getBoundingClientRect().bottom : 0
      document.documentElement.style.setProperty('--sbs-chat-top', `${Math.round(Math.max(bottom, 0) + 10)}px`)
      this.showChat()
    }
    this.sync()
  }

  // Right after the chat is defined it can ignore show() for a moment; try again briefly until it opens.
  showChat(tries = 10) {
    this.chat.show()
    if (tries > 1) {
      setTimeout(() => {
        if (!this.chat.open) this.showChat(tries - 1)
      }, 300)
    }
  }

  // Once per visit (per tab), from the icon that's on screen, within the team's hours, unless the chat is open or
  // there's an unread reply (which gets its own bubble). Skipped (not used up) if the icon is off screen by then, e.g.
  // the phone menu is open: a later page can still nudge.
  async nudge() {
    if (nudgeTaken || this.hasAttribute('data-no-nudge') || !this.iconOnScreen()) return
    try {
      if (sessionStorage.getItem(NUDGED_KEY)) return
    } catch {
      return
    }
    let logic
    try {
      logic = await import(this.dataset.logicSrc)
    } catch {
      return // no rule, no nudge: the icon still works
    }
    const hours = {
      from: Number(this.dataset.nudgeFrom),
      until: Number(this.dataset.nudgeUntil),
      weekdays: this.hasAttribute('data-nudge-weekdays')
    }
    if (!logic.inNudgeHours(new Date(), hours)) return
    nudgeTaken = true

    setTimeout(() => {
      if (!this.isConnected || this.chat.open || readUnread() || !this.iconOnScreen()) return
      try {
        sessionStorage.setItem(NUDGED_KEY, '1')
      } catch {}
      this.showTip('nudge')
    }, NUDGE_DELAY)
  }

  // The icon hops, then the bubble drops out of it as the hop lands (assets/sbs-header-chat.css). "nudge" shows
  // typing dots for a moment before "Need help?" and leaves after a few seconds; "reply" shows "New reply" straight
  // away and stays until it's tapped or the chat is opened.
  showTip(mode) {
    clearTimeout(this.showTimer)
    clearTimeout(this.typedTimer)
    clearTimeout(this.tipTimer)
    clearTimeout(this.leaveTimer)
    this.tip.hidden = true
    this.tip.classList.remove('is-shown', 'is-typed', 'is-leaving')
    this.tip.classList.toggle('is-reply', mode === 'reply')
    this.placeTip()

    this.classList.add('is-nudging')
    setTimeout(() => this.classList.remove('is-nudging'), 1000)

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const typing = mode === 'nudge' && !reduced ? TYPING_TIME : 0
    this.showTimer = setTimeout(() => {
      this.tip.hidden = false
      this.tip.classList.add('is-shown')
      this.typedTimer = setTimeout(() => this.tip.classList.add('is-typed'), typing)
      if (mode === 'nudge') this.tipTimer = setTimeout(() => this.hideTip(), typing + TIP_TIME)
    }, reduced ? 0 : HOP_LANDS)
  }

  // Desktop: the bubble's right edge sits just past the icon. Phones: it lines up with the header's right edge (where
  // the menu icon ends), so it hugs the side of the screen. Either way the pointer stays under the icon's middle.
  placeTip() {
    const wrapperRight = this.getBoundingClientRect().right
    const icon = this.button.getBoundingClientRect()
    let tipRight = wrapperRight + 6
    const container = this.closest('.page-width')
    if (container && window.matchMedia('(max-width: 768px)').matches) {
      tipRight = container.getBoundingClientRect().right - parseFloat(getComputedStyle(container).paddingRight)
    }
    this.style.setProperty('--sbs-chat-tip-right', `${Math.round(wrapperRight - tipRight)}px`)
    this.style.setProperty('--sbs-chat-icon-center', `${Math.round(tipRight - (icon.left + icon.width / 2))}px`)
  }

  hideTip() {
    clearTimeout(this.showTimer)
    clearTimeout(this.typedTimer)
    clearTimeout(this.tipTimer)
    if (this.tip.hidden) return
    this.tip.classList.add('is-leaving')
    this.leaveTimer = setTimeout(() => {
      this.tip.hidden = true
      this.tip.classList.remove('is-shown', 'is-typed', 'is-leaving')
    }, 250)
  }
}

if (!customElements.get('sbs-header-chat')) customElements.define('sbs-header-chat', SbsHeaderChat)
